import React, { useCallback } from 'react';
import { Button } from 'react-bootstrap';
import { NotificationModel } from '../../../services/notification/NotificationService';
import useDoActionNotification from '../../../hooks/api/notification/useDoActionNotification';
import { FormattedMessage } from 'react-intl';
import useOrganizationsList from '../../../hooks/api/organization/useOrganizationsList';

export interface OrganizationInvitationNotificationProps {
  id?: string;
  organization: { id: string; name: string; avatar: string };
  invitedBy: { id: string; name: string; avatar: string };
  createdAt?: string | Date;
  onDecline?: (id?: string) => void;
  notification: NotificationModel;
  refresh: () => void;
  disabled?: boolean;
}

const OrganizationInvitationNotification: React.FC<OrganizationInvitationNotificationProps> = ({
  id,
  organization,
  invitedBy,
  createdAt,
  onDecline,
  notification,
  refresh,
  disabled = false,
}) => {
  const { doActionNotification } = useDoActionNotification();
  const { getOrganizations } = useOrganizationsList();

  const handleAccept = useCallback(
    async (notification: NotificationModel) => {
      await doActionNotification({ action: 'ACCEPT', notification });
      refresh();
      getOrganizations();
    },
    [id],
  );

  const handleDecline = useCallback(
    async (notification: NotificationModel) => {
      await doActionNotification({ action: 'DECLINE', notification });
      refresh();
    },
    [onDecline, id, disabled],
  );

  return (
    <div className="d-flex flex-row align-items-center flex-wrap" role="group" aria-label="Organization invitation">
      <img className="avatar rounded-circle mb-3" src={organization.avatar}></img>
      <div className="me-2 flex-1 mb-3">
        <span>
          <FormattedMessage
            id="notificationList.organizationInvitationMessage"
            values={{
              b: (chunks) => <strong>{chunks}</strong>,
              invitedBy: invitedBy.name,
              organizationName: organization.name,
            }}
          />
        </span>

        {createdAt && (
          <time className="timestamp" dateTime={typeof createdAt === 'string' ? createdAt : createdAt.toISOString()}>
            {typeof createdAt === 'string' ? createdAt : createdAt.toLocaleString()}
          </time>
        )}
      </div>

      <div className="d-flex flex-row mb-3">
        {notification.content.status == 'PENDING' && (
          <>
            <Button
              className="me-1"
              type="button"
              onClick={() => handleAccept(notification)}
              disabled={disabled}
              aria-label="Accept invitation"
            >
              Accept
            </Button>
            <Button
              variant={'secondary'}
              type="button"
              onClick={() => handleDecline(notification)}
              disabled={disabled}
              aria-label="Decline invitation"
            >
              Decline
            </Button>
          </>
        )}
        {notification.content.status == 'ACCEPTED' && <span className="badge badge-success">Accepted</span>}
        {notification.content.status == 'DECLINED' && <span className="badge badge-danger   ">Declined</span>}
      </div>
    </div>
  );
};

export default OrganizationInvitationNotification;
